import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { getErrorMessage, openNotification } from '../../../utils/helper';
import { useTheme } from '@mui/material/styles';
import { Cross } from '../../../components/icons/Cross';
import * as Yup from 'yup';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { mutateConfig } from '../../../api/queryBase';
import useBeneficiaries from '../../../customhooks/beneficiaries/useBeneficiaries';
import CountrySelect from '../../../components/CountrySelect';
import { CurrencySelect } from '../../../components/CurrencySelect';
import CustomTextField from '../../../components/CustomTextInput';
import { SuitUIButton } from '../../../components/SuitUIButton';
import SuiteUITabs from '../../../components/SuiteUiTabs';

interface AddBeneficiaryProps {
    closeDrawer: () => void;
    showDrawer: boolean;
    reRunGetBeneficiaries: () => void;
    beneficiaryId?: number;
    setBeneficiaryId: Dispatch<SetStateAction<number | null>>;
}

export interface AddBeneficiariesValues {
    beneficiaryFirstName: string;
    beneficiaryLastName: string;
    accountNumber: string;
    beneficiaryCompanyName: string;
    country: string;
    currency: string;
    swiftCode: string;
    beneficiaryEmailAddress: string;
    beneficiaryCountryCode: string;
}

const commonFields = {
    accountNumber: Yup.string().required('Account number is required'),
    country: Yup.string().required('Country is required'),
    currency: Yup.string().required('Currency is required'),
    swiftCode: Yup.string().required('Swift code is required'),
    beneficiaryEmailAddress: Yup.string()
        .email('Invalid email')
        .required('Email address is required'),
    beneficiaryCountryCode: Yup.string().required('Country is required')
};

const individualValidationSchema = Yup.object().shape({
    ...commonFields,
    beneficiaryFirstName: Yup.string().required('First name is required'),
    beneficiaryLastName: Yup.string().required('Last name is required')
});

const companyValidationSchema = Yup.object().shape({
    ...commonFields,
    beneficiaryCompanyName: Yup.string().required('Company name is required')
});

const AddBeneficiaryDrawer = ({
    closeDrawer,
    showDrawer,
    beneficiaryId,
    setBeneficiaryId
}: AddBeneficiaryProps) => {
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { beneficiaries } = useBeneficiaries();

    const beneficiaryDetails = beneficiaries?.filter(
        (item) => item.id === beneficiaryId
    )[0];

    const { user } = useSelector((state: RootState) => state.user);
    const [tabValue, setTabValue] = useState('company');

    const mutation = mutateConfig({ method: beneficiaryId ? 'put' : 'post' });
    const { mutate, isLoading } = mutation({
        url: beneficiaryId
            ? `/corporate/${user?.corporate_id}/beneficiary/${beneficiaryId}`
            : `/corporate/${user?.corporate_id}/beneficiary/create`
    });

    const handleSubmit = async (
        values: AddBeneficiariesValues,
        { resetForm }: FormikHelpers<AddBeneficiariesValues>
    ) => {
        if (!user) {
            return;
        }

        mutate(
            { ...values, type: tabValue?.toLowerCase() },
            {
                onSettled: (_, error) => {
                    if (error) {
                        openNotification(
                            'error',
                            getErrorMessage(error) || 'Something went wrong'
                        );
                        return;
                    }
                    openNotification('success', 'Updated successfully');

                    closeDrawer();
                    setBeneficiaryId(null);
                    resetForm();
                }
            }
        );
    };

    const initialValues: AddBeneficiariesValues = {
        beneficiaryFirstName: beneficiaryDetails?.beneficiaryFirstName ?? '',
        beneficiaryLastName: beneficiaryDetails?.beneficiaryLastName ?? '',
        accountNumber: beneficiaryDetails?.accountNumber ?? '',
        beneficiaryCompanyName:
            beneficiaryDetails?.beneficiaryCompanyName ?? '',
        country: beneficiaryDetails?.beneficiaryCountryName ?? '',
        currency: beneficiaryDetails?.currencyCode ?? '',
        swiftCode: beneficiaryDetails?.swiftCode ?? '',
        beneficiaryEmailAddress: '',
        beneficiaryCountryCode: beneficiaryDetails?.beneficiaryCountryCode ?? ''
    };
    useEffect(() => {
        if (beneficiaryDetails) {
            setTabValue(
                beneficiaryDetails?.beneficiaryEntityType.toLowerCase()
            );
        }
    }, [beneficiaryDetails]);

    return (
        <div>
            {showDrawer ? (
                <div
                    className='active bg-appcolorwhite rounded-[50%] top-[1.2rem] right-[32rem] fixed p-4 z-[4000] cursor-pointer'
                    onClick={() => {
                        closeDrawer();
                        setBeneficiaryId(null);
                    }}
                >
                    <Cross />
                </div>
            ) : null}

            <Drawer
                anchor={isMobile ? 'bottom' : 'right'}
                open={showDrawer}
                onClose={() => {
                    closeDrawer();
                    setBeneficiaryId(null);
                }}
                sx={{
                    '.MuiDrawer-paperAnchorRight': {
                        width: {
                            xs: '95%',
                            lg: '30rem'
                        }
                    }
                }}
            >
                <div className='px-[16px] pt-[24px] pb-[80px]  lg:px-[3.2rem] lg:py-[2.1rem]'>
                    <h1 className='text-base text-ebony lg:text-[24px] lg:leading-[23px] lg:text-appcolorblacktwo lg:font-semibold'>
                        {beneficiaryId
                            ? 'Update Beneficiary'
                            : 'Add Beneficiary'}
                    </h1>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={
                            tabValue === 'company'
                                ? companyValidationSchema
                                : individualValidationSchema
                        }
                        onSubmit={handleSubmit}
                        validateOnMount={true}
                    >
                        {({ errors, isValid, values, setFieldValue }) => (
                            <Form autoComplete='off' noValidate>
                                <div className=''>
                                    <p className='text-steelBlue text-sm font-medium mt-[40px] mb-[8px]'>
                                        Select beneficiary type
                                    </p>

                                    <SuiteUITabs
                                        tabs={[
                                            {
                                                label: 'Company',
                                                value: 'company'
                                            },
                                            {
                                                label: 'Individual',
                                                value: 'individual'
                                            }
                                        ]}
                                        activeTab={tabValue}
                                        setActiveTab={(val: string) => {
                                            setTabValue(val);
                                            setFieldValue(
                                                'beneficiaryCompanyName',
                                                ''
                                            );
                                        }}
                                    />

                                    <div className='flex flex-col w-full gap-[16px] mt-4'>
                                        {tabValue === 'individual' && (
                                            <>
                                                <div>
                                                    <Field
                                                        as={CustomTextField}
                                                        error={
                                                            !!errors.beneficiaryFirstName
                                                        }
                                                        helperText={
                                                            errors.beneficiaryFirstName ??
                                                            undefined
                                                        }
                                                        margin='normal'
                                                        required
                                                        fullWidth
                                                        id='addbeneficiary-beneficiaryFirstName'
                                                        label='First Name'
                                                        name='beneficiaryFirstName'
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            setFieldValue(
                                                                'beneficiaryFirstName',
                                                                e.target.value
                                                            );
                                                            setFieldValue(
                                                                'beneficiaryCompanyName',
                                                                `${
                                                                    values?.beneficiaryLastName ||
                                                                    ''
                                                                } ${
                                                                    e.target
                                                                        .value
                                                                }`
                                                            );
                                                        }}
                                                    />
                                                </div>

                                                <Field
                                                    as={CustomTextField}
                                                    error={
                                                        !!errors?.beneficiaryLastName
                                                    }
                                                    helperText={
                                                        errors?.beneficiaryLastName ??
                                                        undefined
                                                    }
                                                    margin='normal'
                                                    required
                                                    fullWidth
                                                    id='addbeneficiary-beneficiaryLastName'
                                                    label='Last Name'
                                                    name='beneficiaryLastName'
                                                    onChange={(
                                                        e: React.ChangeEvent<HTMLInputElement>
                                                    ) => {
                                                        setFieldValue(
                                                            'beneficiaryLastName',
                                                            e.target.value
                                                        );
                                                        setFieldValue(
                                                            'beneficiaryCompanyName',
                                                            `${
                                                                e.target
                                                                    .value || ''
                                                            } ${
                                                                values.beneficiaryFirstName
                                                            }`
                                                        );
                                                    }}
                                                />
                                            </>
                                        )}

                                        {tabValue === 'company' && (
                                            <Field
                                                as={CustomTextField}
                                                error={
                                                    !!errors.beneficiaryCompanyName
                                                }
                                                helperText={
                                                    errors.beneficiaryCompanyName ??
                                                    undefined
                                                }
                                                margin='normal'
                                                required
                                                fullWidth
                                                id='addbeneficiary-beneficiaryCompanyName'
                                                label='Company Name'
                                                name='beneficiaryCompanyName'
                                            />
                                        )}

                                        <Field
                                            as={CustomTextField}
                                            error={
                                                !!errors.beneficiaryEmailAddress
                                            }
                                            helperText={
                                                errors?.beneficiaryEmailAddress ??
                                                undefined
                                            }
                                            margin='normal'
                                            required
                                            fullWidth
                                            id='addbeneficiary-beneficiaryEmailAddress'
                                            label='Email'
                                            name='beneficiaryEmailAddress'
                                            type='email'
                                        />

                                        <div className='flex flex-col w-full gap-[16px] lg:flex-row'>
                                            <div className='w-full'>
                                                <CountrySelect
                                                    helperText={
                                                        errors.beneficiaryCountryCode ??
                                                        undefined
                                                    }
                                                    error={
                                                        !!errors.beneficiaryCountryCode
                                                    }
                                                    onHandleChange={(value) => {
                                                        if (value !== null) {
                                                            setFieldValue(
                                                                'beneficiaryCountryCode',
                                                                value.countryCode2
                                                            );
                                                            setFieldValue(
                                                                'country',
                                                                value.name
                                                            );
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <CurrencySelect
                                                    allOptions={true}
                                                    helperText={
                                                        errors.currency ??
                                                        undefined
                                                    }
                                                    error={!!errors.currency}
                                                    onHandleChange={(value) => {
                                                        if (value !== null) {
                                                            setFieldValue(
                                                                'currency',
                                                                value.currencyCode
                                                            );
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <Field
                                            as={CustomTextField}
                                            error={!!errors?.swiftCode}
                                            helperText={
                                                errors?.swiftCode ?? undefined
                                            }
                                            margin='normal'
                                            required
                                            fullWidth
                                            id='addbeneficiary-swiftCode'
                                            label='SWIFT Code/Sort Code'
                                            name='swiftCode'
                                        />
                                        <Field
                                            as={CustomTextField}
                                            error={!!errors.accountNumber}
                                            helperText={
                                                errors.accountNumber ??
                                                undefined
                                            }
                                            margin='normal'
                                            required
                                            fullWidth
                                            id='addbeneficiary-accountNumber'
                                            label='IBAN/Account Number'
                                            name='accountNumber'
                                        />

                                        <div className='flex items-center gap-[16px]'>
                                            <SuitUIButton
                                                text='Cancel'
                                                onClick={() => {
                                                    closeDrawer();
                                                    setBeneficiaryId(null);
                                                }}
                                                type='button'
                                                invert
                                                fullWidth
                                                style='border border-gray300 text-steelBlue font-medium text-sm leading-[20px]'
                                            />

                                            <SuitUIButton
                                                text={
                                                    beneficiaryId
                                                        ? 'Update'
                                                        : 'Add Beneficiary'
                                                }
                                                type='submit'
                                                fullWidth
                                                isLoading={isLoading}
                                                disabled={!isValid}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </Drawer>
        </div>
    );
};

export default AddBeneficiaryDrawer;
